import * as XLSX from 'xlsx';
import type { WineListFormat } from '../types/venue';
import type { WineListSyncExportResponse } from '../services/wineListSyncService';

/**
 * W8 — wine list sync template + export helpers.
 *
 * Mirrors the inventoryImportTemplate / excelUtils pattern: header on row 1,
 * example values on row 2, real data on row 3+. The backend treats row 2 as
 * data — users replace the example row with their own rows.
 */

interface ColumnDef {
  header: string;
  example: string;
}

// W8 (revised) — one column per critic source instead of a single Score Source/Score pair.
const STATIC_COLUMNS: ColumnDef[] = [
  { header: 'WineListItem ID',   example: '' },
  { header: 'Wine ID',           example: '' },
  { header: 'Inventory Item ID', example: '' },
  { header: 'Section',           example: 'Burgundy' },
  { header: 'Position',          example: '0' },
  { header: 'Wine Name',         example: 'Bourgogne Rouge' },
  { header: 'Producer',          example: 'Domaine X' },
  { header: 'Vintage',           example: '2020' },
  { header: 'Region',            example: 'Côte de Beaune' },
  { header: 'Bottle Size',       example: '750' },
  { header: 'Bottle Price',      example: '95' },
  { header: 'Currency',          example: 'EUR' },
  { header: 'Visibility',        example: 'LIVE' },
  { header: 'Notes',             example: 'Drinking beautifully' },
  { header: 'External Link',     example: '' },
  { header: 'Score JS',          example: '' },
  { header: 'Score WA',          example: '' },
  { header: 'Score WS',          example: '' },
  { header: 'Score AV',          example: '' },
];

/** Build the format-column header used by the import: "Format: Glass 150ml". */
export const formatColumnHeader = (name: string, volumeMl: number): string =>
  `Format: ${name} ${volumeMl}ml`;

const buildColumns = (formats: WineListFormat[]): ColumnDef[] => {
  const formatCols: ColumnDef[] = formats.map(f => ({
    header: formatColumnHeader(f.name, f.volumeMl),
    example: '',
  }));
  return [...STATIC_COLUMNS, ...formatCols];
};

const writeXlsx = (filename: string, columns: ColumnDef[], dataRows: Array<Array<string | number>>) => {
  const wb = XLSX.utils.book_new();
  const aoa: Array<Array<string | number>> = [
    columns.map(c => c.header),
    columns.map(c => c.example),
    ...dataRows,
  ];
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols'] = columns.map(c => ({ wch: Math.max(c.header.length, String(c.example).length) + 4 }));
  XLSX.utils.book_append_sheet(wb, ws, 'Wine List');
  XLSX.writeFile(wb, filename);
};

/** Download a blank template with current format columns at the end. */
export function downloadWineListSyncTemplate(formats: WineListFormat[]): void {
  writeXlsx('wine-list-sync-template.xlsx', buildColumns(formats), []);
}

/** Download the existing items in the same shape so admins can edit and re-upload. */
export function downloadExistingItemsAsImport(
  exportResp: WineListSyncExportResponse,
  formats: WineListFormat[],
): void {
  const columns = buildColumns(formats);
  const headerToValue = (h: string, row: Record<string, string | number>): string | number =>
    row[h] ?? '';
  const dataRows: Array<Array<string | number>> = exportResp.rows.map(row =>
    columns.map(c => headerToValue(c.header, row)),
  );
  writeXlsx('wine-list-export.xlsx', columns, dataRows);
}
